import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, DropButton, Menu, Pagination, ResponsiveContext } from 'grommet';
import { Filter, Menu as MenuIcon, Share } from 'grommet-icons';
import { ApiError, FacilityService, TListFacilitiesRequest } from '/src/api';
import {
  AddButton,
  Box,
  BoxProps,
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  DeleteMultipleFacilitiesModal,
  FacilityIcon,
  Filters,
  FiltersDropdown,
  Link,
  LoadingSpinner,
  RetireMultipleFacilitiesModal,
  SearchInput,
  Text,
} from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState, useSyncDraft } from '/src/hooks/useRestorableListState';
import { Facility, TClientId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { tooltipMessages } from '/src/lib/tooltips';
import { EntityStatus, TResponseMetadata } from '/src/lib/types';
import { getIsMobile, pxToRem } from '/src/utils';

type FacilityListQueryState = {
  page: number;
  search: string;
  is_active: string | undefined; // 'true' | 'false' | '' | undefined
  program_id: number | undefined;
  utility_id: number | undefined;
  address_region_id: number | undefined;
};

const FACILITY_LIST_DEFAULTS: FacilityListQueryState = {
  page: 1,
  search: '',
  is_active: 'true',
  program_id: undefined,
  utility_id: undefined,
  address_region_id: undefined,
};

const FACILITY_LIST_CLEARED: FacilityListQueryState = {
  ...FACILITY_LIST_DEFAULTS,
  is_active: '',
};

export type FacilityListProps = {
  clientId: TClientId;
  id?: string;
  title?: string;
  facilities?: Facility[];
  fetchFacilities?: (page?: number) => void;
  metadata?: TResponseMetadata;
  isLoading?: boolean;
  showAddButton?: boolean;
  showShareButton?: boolean;
  showFilters?: boolean;
  showSearchInput?: boolean;
  hideHeader?: boolean;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  boxProps?: BoxProps;
  dataTour?: string;
  surfaceId?: string;
  standalone?: boolean;
  mode?: 'query' | 'controlled';
};

export const FacilityList: React.FC<FacilityListProps> = (props) => {
  const {
    clientId,
    id,
    title,
    showFilters,
    showSearchInput,
    showAddButton,
    showShareButton,
    isLoading: propIsLoading,
    facilities: propFacilities,
    fetchFacilities: propFetchFacilities,
    currentPage: propCurrentPage,
    setCurrentPage: propSetCurrentPage,
    metadata: propMetadata,
    hideHeader,
    boxProps,
    dataTour,
    surfaceId,
    standalone = false,
    mode = 'query',
  } = props;

  const isPropControlled = mode === 'controlled';

  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const screenSize = useContext(ResponsiveContext);
  const queryClient = useQueryClient();
  const tooltips = tooltipMessages();
  const { t } = useTranslation();
  const navigate = useNavigate();

  /** Restorable state **/
  const restorableList = useRestorableListState<FacilityListQueryState>({
    surfaceId: surfaceId || 'facility-list',
    defaults: FACILITY_LIST_DEFAULTS,
    standalone,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  /** Draft state **/
  const [draftSearch, setDraftSearch] = useState(queryState.search);
  const [draftIsActive, setDraftIsActive] = useState<string | undefined>(queryState.is_active);
  const [draftFilters, setDraftFilters] = useState<Record<string, number | undefined>>({
    program_id: queryState.program_id,
    utility_id: queryState.utility_id,
    address_region_id: queryState.address_region_id,
  });

  /** Modal state **/
  const [showRetireMultipleModal, setShowRetireMultipleModal] = useState(false);
  const [showDeleteMultipleModal, setShowDeleteMultipleModal] = useState(false);
  const [userCanRetire, setUserCanRetire] = useState(false);
  const [userCanDelete, setUserCanDelete] = useState(false);
  const [isMenuOpen] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const [isFiltersMenuHovered, setIsFiltersMenuHovered] = useState(false);

  /** Computed **/
  const isMobile = getIsMobile(screenSize);

  const request: TListFacilitiesRequest = useMemo(
    () => ({
      client_id: clientId,
      page: queryState.page,
      address_line1: queryState.search || undefined,
      is_active: queryState.is_active === 'true' ? true : queryState.is_active === 'false' ? false : undefined,
      program_id: queryState.program_id,
      utility_id: queryState.utility_id,
      address_region_id: queryState.address_region_id ? String(queryState.address_region_id) : undefined,
    }),
    [queryState, clientId]
  );

  /** React Query **/
  const {
    data: queryData,
    isLoading: queryIsLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['facilities', userStore.user?.id, request],
    queryFn: () => FacilityService.list(request),
    enabled: !isPropControlled && !!userStore.user && !!clientId && !Number.isNaN(clientId),
    placeholderData: (prev) => prev,
  });

  const queryFacilities = queryData?.data;
  const queryMetadata = queryData?.meta;

  /** Toast on query error **/
  useEffect(() => {
    if (queryError && !isPropControlled) {
      globalStore.handleApiError(queryError as ApiError, toastMessages.listFacilities.error);
    }
  }, [queryError]);

  /** Check facilities for retire (active) and delete (unfinalized) permissions **/
  useEffect(() => {
    if (isPropControlled || !clientId || Number.isNaN(clientId)) return;
    if (!userStore.isFuseAdmin) {
      setUserCanRetire(false);
      setUserCanDelete(false);
      return;
    }

    let cancelled = false;
    const setIfCurrent = (setter: (value: boolean) => void) => (value: boolean) => {
      if (!cancelled) setter(value);
    };

    const hasMatches = (filter: Partial<TListFacilitiesRequest>) =>
      FacilityService.list({ client_id: clientId, limit: 1, page: 1, ...filter })
        .then((facilityList) => (facilityList.meta?.total ?? 0) > 0)
        .catch(() => false);

    hasMatches({ is_active: true }).then(setIfCurrent(setUserCanRetire));
    hasMatches({ is_active: true, is_finalized: false }).then(setIfCurrent(setUserCanDelete));

    return () => {
      cancelled = true;
    };
  }, [clientId, isPropControlled, userStore.isFuseAdmin]);

  /** Resolved values **/
  const _facilities = propFacilities ?? queryFacilities;
  const _metadata = propMetadata ?? queryMetadata;
  const _currentPage = propCurrentPage ?? queryState.page;
  const _isLoading = propIsLoading ?? (isPropControlled ? false : queryIsLoading || isFetching);
  const isRefetching = isFetching && !queryIsLoading;
  const hasFacilities = !!_facilities?.length;
  const lastQuery = queryState.search;

  /** Methods **/
  const search = useCallback(
    (query?: string) => {
      setQueryState({ search: query || '', page: 1 });
    },
    [setQueryState]
  );

  const applyFilters = useCallback(() => {
    setQueryState({
      is_active: draftIsActive,
      program_id: draftFilters.program_id,
      utility_id: draftFilters.utility_id,
      address_region_id: draftFilters.address_region_id,
      page: 1,
    });
    setIsFiltersMenuOpen(false);
  }, [setQueryState, draftIsActive, draftFilters]);

  const clearFilters = useCallback(() => {
    setDraftIsActive(FACILITY_LIST_CLEARED.is_active);
    setDraftFilters({
      program_id: FACILITY_LIST_CLEARED.program_id,
      utility_id: FACILITY_LIST_CLEARED.utility_id,
      address_region_id: FACILITY_LIST_CLEARED.address_region_id,
    });
    setDraftSearch('');
    setQueryState(FACILITY_LIST_CLEARED);
    setIsFiltersMenuOpen(false);
  }, [setQueryState]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (isPropControlled) {
        propSetCurrentPage?.(page);
        propFetchFacilities?.(page);
      } else {
        setQueryState({ page });
      }
    },
    [isPropControlled, propSetCurrentPage, propFetchFacilities, setQueryState]
  );

  /** shouldRefresh callback for modals **/
  const setShouldRefresh = useCallback(
    (value: boolean) => {
      if (value) {
        refetch();
        queryClient.invalidateQueries({ queryKey: ['equipment'] });
      }
    },
    [refetch, queryClient]
  );

  /** Sync page from API response **/
  useEffect(() => {
    if (queryMetadata && !isPropControlled && queryMetadata.current_page !== queryState.page) {
      setQueryState({ page: queryMetadata.current_page });
    }
  }, [queryMetadata?.current_page]);

  /** Sync drafts when queryState changes **/
  useSyncDraft(queryState, setDraftSearch, (s) => s.search);
  useSyncDraft(queryState, setDraftIsActive, (s) => s.is_active);
  useSyncDraft(queryState, setDraftFilters, (s) => ({
    program_id: s.program_id,
    utility_id: s.utility_id,
    address_region_id: s.address_region_id,
  }));

  /** Render **/
  const renderFleetOverview = (facility: Facility) => {
    if (!facility.num_equipment_per_category.length) {
      return (
        <Box>
          <Text>-</Text>
        </Box>
      );
    }

    let hasItem = false;

    const overview = (
      <Box
        direction={isMobile ? 'column' : 'row'}
        wrap
        gap={isMobile ? '3px' : '6px'}
        margin={{ vertical: isMobile ? pxToRem(12) : undefined }}
      >
        {facility.num_equipment_per_category.map(({ category_name, num_equipment }) => {
          if (!category_name || !num_equipment) return;
          hasItem = true;
          return category_name && num_equipment ? (
            <Box
              key={category_name}
              direction="row"
              margin={{ vertical: pxToRem(3) }}
              border={{ side: 'all', size: 'small', color: 'light-2' }}
              background="white"
              gap={pxToRem(12)}
              pad={{ vertical: pxToRem(4), left: pxToRem(10), right: pxToRem(6) }}
              style={{ boxShadow: 'rgba(0, 0, 0, 0.2) 0 10px 10px -10px' }}
              round={pxToRem(4)}
            >
              <Box justify="center" flex={isMobile ? true : undefined}>
                <Text size={pxToRem(12)} lineHeight={pxToRem(12)}>
                  {category_name}
                </Text>
              </Box>
              <Box align="center" justify="center">
                <Box
                  style={{ minWidth: pxToRem(28) }}
                  pad={pxToRem(2.8)}
                  round={pxToRem(4)}
                  align="center"
                  justify="center"
                  background="accent-1"
                >
                  <Text color="white" weight={500} size={pxToRem(12)} lineHeight={pxToRem(12)}>
                    {num_equipment}
                  </Text>
                </Box>
              </Box>
            </Box>
          ) : undefined;
        })}
      </Box>
    );
    return hasItem ? (
      overview
    ) : (
      <Box>
        <Text>-</Text>
      </Box>
    );
  };

  const facilityMenuItems = [
    {
      label: t('facility:addFacility'),
      onClick: () => navigate(`/clients/${clientId}/facilities/create`),
    },
    {
      label: t('facility:retireFacilities'),
      onClick: () => userCanRetire && setShowRetireMultipleModal(true),
      disabled: !userCanRetire,
    },
    {
      label: t('facility:deleteFacilities'),
      onClick: () => userCanDelete && setShowDeleteMultipleModal(true),
      disabled: !userCanDelete,
    },
  ]
    .filter((item) => item.label !== t('facility:addFacility') || showAddButton)
    .filter((item) => item.label !== t('facility:deleteFacilities') || userCanDelete)
    .filter((item) => item.label !== t('facility:retireFacilities') || userCanRetire);

  return (
    <Box id={id} direction="row" gap="medium" fill="horizontal" data-tour={dataTour}>
      <Card {...boxProps}>
        {!hideHeader && (
          <CardHeader
            title={title || t('facility:facilityList')}
            icon={<FacilityIcon />}
            {...(showShareButton && {
              button: <Share size="16px" />,
              buttonNavigatePath: `/clients/${clientId}/facilities`,
              buttonTooltip: tooltipMessages().facilityPageLink,
            })}
          >
            <Box direction="row" gap="small">
              {showSearchInput && (
                <SearchInput
                  searchQuery={draftSearch}
                  placeholder={t('facility:searchByAddress')}
                  setSearchQuery={setDraftSearch}
                  onSearch={search}
                  isSearching={isRefetching}
                  data-tour="facility-search"
                />
              )}
              <DropButton
                plain
                icon={<Filter color={isFiltersMenuHovered || isFiltersMenuOpen ? 'brand' : undefined} />}
                open={isFiltersMenuOpen}
                onOpen={() => setIsFiltersMenuOpen(true)}
                onClose={() => setIsFiltersMenuOpen(false)}
                onMouseOver={() => setIsFiltersMenuHovered(true)}
                onMouseOut={() => setIsFiltersMenuHovered(false)}
                tip={tooltips.filterFacilities}
                dropAlign={{ right: 'right', top: 'bottom' }}
                dropContent={
                  <FiltersDropdown
                    isFiltering={isRefetching}
                    onSubmit={() => applyFilters()}
                    onClear={() => clearFilters()}
                    filters={[
                      {
                        label: t('facility:activityStatus'),
                        value: draftIsActive ?? '',
                        setValue: (value) => setDraftIsActive((value as string) || undefined),
                        options: [
                          { label: t('common:all'), value: '' },
                          { label: t('common:active'), value: 'true' },
                          { label: t('common:retired'), value: 'false' },
                        ],
                      },
                    ]}
                  />
                }
              />
              {userStore.isFuseAdmin
                ? facilityMenuItems.length > 0 && (
                    <Box>
                      <Menu
                        icon={<MenuIcon color={isMenuHovered || isMenuOpen ? 'brand' : undefined} />}
                        onMouseOver={() => setIsMenuHovered(true)}
                        onMouseOut={() => setIsMenuHovered(false)}
                        tip={t('facility:facilityActions')}
                        dropAlign={{ top: 'bottom', right: 'right' }}
                        items={facilityMenuItems}
                      />
                    </Box>
                  )
                : showAddButton && (
                    <AddButton label={t('facility:addFacility')} targetUrl={`/clients/${clientId}/facilities/create`} />
                  )}
            </Box>
          </CardHeader>
        )}
        <CardBody pad="none" gap="none">
          {_isLoading && <LoadingSpinner />}
          {!_isLoading && (
            <Box elevation="small">
              <DataTable
                id="facility_list_data_table"
                pad={{ horizontal: '1.5rem' }}
                columns={[
                  {
                    property: 'id',
                    primary: true,
                    size: '24%',
                    header: <DataTableHeader title={t('common:address')} />,
                    render: (facility: Facility) => (
                      <Box pad={{ vertical: '1rem' }}>
                        <Link
                          to={`/clients/${
                            !!clientId && !Number.isNaN(clientId) ? clientId : (facility.client_id ?? '')
                          }/facilities/${facility.id}`}
                          textDecoration="none"
                        >
                          <Box>
                            <Text
                              size="medium"
                              weight={700}
                              color="accent-1"
                              fontFamily="Lato, sans-serif"
                              lineHeight="1.25rem"
                            >
                              {facility.address_line1}
                            </Text>
                            <Text size="small" color="accent-1" fontFamily="Lato, sans-serif" lineHeight="1rem">
                              {facility.address_city},{' '}
                              {
                                (facility.address_region ?? globalStore.getRegionById(facility.address_region_id))
                                  ?.short_code
                              }{' '}
                              {facility.address_post_code}
                            </Text>
                          </Box>
                        </Link>
                      </Box>
                    ),
                  },
                  {
                    property: 'is_active',
                    header: <DataTableHeader title={t('common:status')} />,
                    render: (facility: Facility) => (
                      <DataTableItem value={facility.is_active ? EntityStatus.Active : EntityStatus.Retired} />
                    ),
                  },
                  {
                    property: 'program.name',
                    size: '10%',
                    header: <DataTableHeader title={t('common:program')} />,
                    render: (facility: Facility) => (
                      <Text size="medium" fontFamily="Lato, sans-serif">
                        {facility.program?.name}
                      </Text>
                    ),
                  },
                  {
                    property: 'utility.name',
                    size: '28%',
                    header: <DataTableHeader title={t('facility:utility')} />,
                    render: (facility: Facility) => (
                      <Text size="medium" fontFamily="Lato, sans-serif">
                        {facility.utility?.name}
                      </Text>
                    ),
                  },
                  {
                    property: 'fleet_overview',
                    size: '36%',
                    sortable: false,
                    header: <DataTableHeader title={t('facility:assetOverview')} />,
                    align: 'start',
                    render: (facility: Facility) => renderFleetOverview(facility),
                  },
                ]}
                data={_facilities}
                sortable
                background={['light-6', 'white']}
                border={{ color: 'light-2', side: 'bottom', size: 'small' }}
              />
            </Box>
          )}
          {!hasFacilities && !_isLoading && (
            <Box pad={{ horizontal: '1.5rem', vertical: '2rem' }} background="light-6" justify="center">
              <Text alignSelf="center" size="medium" fontFamily="Lato, sans-serif">
                No facilities found{lastQuery ? ` with the name "${lastQuery}"` : ''}.
              </Text>
            </Box>
          )}
          {!!_metadata && !!_currentPage && (
            <Box pad="1.5rem" flex="grow">
              <Pagination
                alignSelf="center"
                size="small"
                page={_currentPage}
                step={_metadata?.per_page}
                numberItems={_metadata?.total}
                onChange={(e) => handlePageChange(e.page)}
              />
            </Box>
          )}
        </CardBody>
      </Card>
      {showFilters && (
        <Filters
          isFiltering={isRefetching}
          onSubmit={() => applyFilters()}
          onClear={() => clearFilters()}
          filters={[
            {
              label: 'Program',
              value: draftFilters.program_id ?? NaN,
              setValue: (value) => setDraftFilters({ ...draftFilters, program_id: value as number }),
              options: [],
            },
            {
              label: 'Utility',
              value: draftFilters.utility_id ?? NaN,
              setValue: (value) => setDraftFilters({ ...draftFilters, utility_id: value as number }),
              options: [],
            },
            {
              label: 'State/Region',
              value: draftFilters.address_region_id ?? NaN,
              setValue: (value) => setDraftFilters({ ...draftFilters, address_region_id: value as number }),
              options: globalStore.regions
                .filter((region) => !!region.short_code)
                .map((region) => ({ label: region.short_code ?? '', value: region.id })),
            },
          ]}
        />
      )}
      {showRetireMultipleModal && (
        <RetireMultipleFacilitiesModal
          clientId={clientId}
          isVisible={showRetireMultipleModal}
          setIsVisible={setShowRetireMultipleModal}
          setShouldRefresh={setShouldRefresh}
          isCanada={false}
        />
      )}
      {showDeleteMultipleModal && (
        <DeleteMultipleFacilitiesModal
          clientId={clientId}
          isVisible={showDeleteMultipleModal}
          setIsVisible={setShowDeleteMultipleModal}
          setShouldRefresh={setShouldRefresh}
        />
      )}
    </Box>
  );
};
